import type { MouseEvent } from 'react';
import SmartLink from '@/shared/ui/SmartLink';
import { useGatedNav } from '@/shared/utils/useGatedNav';
import { Avatar } from '@/modules/avatar';
import { useProfile } from '@/routes/Providers/ProfileProvider';
import { bottomNavBottom } from '@/shared/constants/bottomNav';
import { useAiAvailable } from '@/shared/utils/useAiAvailable';
import { track } from '@/shared/utils/track';
import { AiIcon, CommunityIcon, CupIcon, HomeIcon } from './icons';

const NAV_LABELS = {
  markets: 'Markets',
  arena: 'Arena',
  community: 'Community',
};

const PILL =
  'overflow-clip rounded-xl bg-background-secondary p-xs shadow-[0px_2px_24px_3px_rgba(24,27,43,0.08),1px_3px_7px_2px_rgba(47,53,77,0.08)]';
const TAB =
  'flex h-12 min-w-px flex-1 items-center justify-center rounded-sm text-gray aria-[current]:text-purple-default';

// `label` is the analytics and onboarding-anchor constant; the accessible
// name is the nav key's translation.
const tabs = [
  { key: 'markets', label: 'Markets', to: '/markets', Icon: HomeIcon },
  { key: 'arena', label: 'Arena', to: '/arena', Icon: CupIcon },
  {
    key: 'community',
    label: 'Community',
    to: '/community',
    Icon: CommunityIcon,
  },
] as const;

// Logged out there is no profile to draw: the slot shows the avatar
// module's shared placeholder (the null-address fallback).
const GUEST = { address: null, avatar: null };

// The AI pill's open state lives in DefaultLayout, shared with the desktop
// launcher in AiWidget. Logged out (the public market and KOL pages) tab taps
// open the sign-up gate instead of navigating — the target pages need an
// account. Only the Markets-active state is designed (a filled home), so
// every tab keeps its one glyph and the active state is the tint alone.
export default function FooterMobile({
  aiOpen,
  onAiToggle,
}: {
  aiOpen: boolean;
  onAiToggle: () => void;
}) {
  const { profile } = useProfile();
  const aiAvailable = useAiAvailable();
  const { onNavClick, openGate, gateEl } = useGatedNav('footer');

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-l z-250 flex items-center gap-l"
      style={{ bottom: bottomNavBottom }}
    >
      <div className={`min-w-px flex-1 ${PILL}`}>
        <div className="flex w-full items-center justify-between overflow-clip rounded-xl bg-background-secondary">
          {tabs.map(({ key, label, to, Icon }) => (
            <SmartLink
              key={label}
              className={TAB}
              navLink
              aria-label={NAV_LABELS[key]}
              data-onboarding={`nav-${label.toLowerCase()}`}
              onClick={(e: MouseEvent) => onNavClick(e, label)}
              to={to}
            >
              <Icon />
            </SmartLink>
          ))}
          {profile ?
            <SmartLink
              className={TAB}
              navLink
              aria-label="Profile"
              data-onboarding="profile-pill"
              onClick={() =>
                track('nav_clicked', { location: 'footer', label: 'Profile' })
              }
              to="/user/$username"
              params={{ username: profile.username }}
            >
              <Avatar size={24} user={profile} />
            </SmartLink>
          : <button
              type="button"
              className={`btn-reset ${TAB}`}
              aria-label="Profile"
              onClick={openGate}
            >
              <Avatar size={24} user={GUEST} />
            </button>
          }
        </div>
      </div>
      {aiAvailable && (
        <button
          type="button"
          className={`btn-reset grid size-14 shrink-0 place-items-center text-purple-default ${PILL}`}
          aria-label={aiOpen ? 'Close AI assistant' : 'Ask AI'}
          aria-expanded={aiOpen}
          onClick={onAiToggle}
        >
          <AiIcon />
        </button>
      )}
      {gateEl}
    </nav>
  );
}
